import { useState } from 'react'
import { Link, useLocation } from 'react-router-dom'
import { useAuth } from '../context/AuthContext'
import { useCart } from '../context/CartContext'

export default function Navbar({ onLoginClick, variant = 'dark' }) {
  const { user, signOut } = useAuth()
  const { itemCount, openCart, cartBounce } = useCart()
  const [isMenuOpen, setIsMenuOpen] = useState(false)
  const location = useLocation()

  const textColor = variant === 'dark' ? 'text-white' : 'text-black'
  const hoverColor = variant === 'dark' ? 'hover:text-gray-300' : 'hover:text-gray-600'
  const bgColor = variant === 'dark' ? 'bg-black' : 'bg-white'
  const activeUnderline = variant === 'dark' ? 'after:bg-white' : 'after:bg-black'

  const isActive = (path) => location.pathname === path || location.pathname.startsWith(path + '/')

  const navLinkClass = (path) => `
    relative text-sm font-medium ${textColor} ${hoverColor} transition-colors
    after:absolute after:left-0 after:bottom-[-4px] after:h-[2px] after:transition-all after:duration-200
    ${activeUnderline}
    ${isActive(path) ? 'after:w-full' : 'after:w-0 hover:after:w-full'}
  `

  const closeMenu = () => setIsMenuOpen(false)

  return (
    <>
      <nav className={`fixed top-0 left-0 right-0 z-50 px-4 md:px-6 py-4 md:py-5 flex items-center justify-between safe-area-inset-top ${isMenuOpen ? bgColor : ''}`}>
        <Link to="/" className={`text-lg ${textColor}`} style={{ fontFamily: "'DM Serif Display', Georgia, serif", letterSpacing: '-0.01em' }} aria-label="Dottedline Home">
          Dottedline.
        </Link>

        {/* Desktop Navigation */}
        <div className="hidden md:flex items-center gap-8">
          <Link to="/about" className={navLinkClass('/about')}>
            About
          </Link>
          <Link to="/magazine" className={navLinkClass('/magazine')}>
            Magazine
          </Link>
          <Link to="/shop" className={navLinkClass('/shop')}>
            Shop
          </Link>
          <Link to="/events" className={navLinkClass('/events')}>
            Events
          </Link>
          <Link to="/stats" className={navLinkClass('/stats')}>
            Stats
          </Link>
          {/* Cart Button */}
          <button
            onClick={openCart}
            className={`relative text-sm font-medium ${textColor} ${hoverColor} transition-colors ${cartBounce ? 'animate-bounce-subtle' : ''}`}
          >
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M16 11V7a4 4 0 00-8 0v4M5 9h14l1 12H4L5 9z" />
            </svg>
            {itemCount > 0 && (
              <span className={`absolute -top-2 -right-2 w-5 h-5 bg-black text-white text-xs rounded-full flex items-center justify-center ${cartBounce ? 'animate-bounce-subtle' : ''}`}>
                {itemCount}
              </span>
            )}
          </button>

          {user ? (
            <div className="flex items-center gap-6">
              <Link
                to="/dashboard"
                className={`text-sm font-medium ${textColor} ${hoverColor} transition-colors`}
              >
                Dashboard
              </Link>
              <button
                onClick={signOut}
                className={`text-sm font-medium ${textColor} ${hoverColor} transition-colors`}
              >
                Logout
              </button>
            </div>
          ) : (
            <button
              onClick={onLoginClick}
              className={`text-sm font-medium ${textColor} ${hoverColor} transition-colors`}
            >
              Login
            </button>
          )}
        </div>

        {/* Mobile Cart & Menu */}
        <div className="flex items-center gap-1 md:hidden">
          <button
            onClick={openCart}
            aria-label={`Shopping cart${itemCount > 0 ? `, ${itemCount} items` : ''}`}
            className={`relative p-3 ${textColor} ${cartBounce ? 'animate-bounce-subtle' : ''}`}
          >
            <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M16 11V7a4 4 0 00-8 0v4M5 9h14l1 12H4L5 9z" />
            </svg>
            {itemCount > 0 && (
              <span className={`absolute top-1 right-1 w-5 h-5 bg-black text-white text-xs rounded-full flex items-center justify-center ${cartBounce ? 'animate-bounce-subtle' : ''}`}>
                {itemCount}
              </span>
            )}
          </button>
          <button
            onClick={() => setIsMenuOpen(!isMenuOpen)}
            aria-label={isMenuOpen ? 'Close menu' : 'Open menu'}
            aria-expanded={isMenuOpen}
            className={`p-3 ${textColor}`}
          >
          {isMenuOpen ? (
            <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M6 18L18 6M6 6l12 12" />
            </svg>
          ) : (
            <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M4 6h16M4 12h16M4 18h16" />
            </svg>
          )}
          </button>
        </div>
      </nav>

      {/* Mobile Menu */}
      {isMenuOpen && (
        <div className={`fixed inset-x-0 top-[60px] bottom-0 z-40 ${bgColor} pt-4 px-6 md:hidden safe-area-inset-bottom overflow-y-auto`}>
          <nav className="flex flex-col gap-4" role="navigation" aria-label="Mobile navigation">
            <Link
              to="/about"
              onClick={closeMenu}
              className={`text-xl sm:text-2xl font-medium ${textColor} py-2`}
            >
              About
            </Link>
            <Link
              to="/magazine"
              onClick={closeMenu}
              className={`text-xl sm:text-2xl font-medium ${textColor} py-2`}
            >
              Magazine
            </Link>
            <Link
              to="/shop"
              onClick={closeMenu}
              className={`text-xl sm:text-2xl font-medium ${textColor} py-2`}
            >
              Shop
            </Link>
            <Link
              to="/events"
              onClick={closeMenu}
              className={`text-xl sm:text-2xl font-medium ${textColor} py-2`}
            >
              Events
            </Link>
            <Link
              to="/stats"
              onClick={closeMenu}
              className={`text-xl sm:text-2xl font-medium ${textColor} py-2`}
            >
              Stats
            </Link>
            <div className="border-t border-gray-200 pt-6 mt-2">
              {user ? (
                <>
                  <Link
                    to="/dashboard"
                    onClick={closeMenu}
                    className={`block text-xl sm:text-2xl font-medium ${textColor} py-2 mb-4`}
                  >
                    Dashboard
                  </Link>
                  <button
                    onClick={() => {
                      signOut()
                      closeMenu()
                    }}
                    className={`text-xl sm:text-2xl font-medium ${textColor} py-2`}
                  >
                    Logout
                  </button>
                </>
              ) : (
                <button
                  onClick={() => {
                    onLoginClick()
                    closeMenu()
                  }}
                  className={`text-xl sm:text-2xl font-medium ${textColor} py-2`}
                >
                  Login
                </button>
              )}
            </div>
          </nav>
        </div>
      )}
    </>
  )
}
